<template>
	<v-card class="fill-height">
		<v-toolbar>
			<v-toolbar-title> {{ eventName }} Properties </v-toolbar-title>
			<template v-slot:extension>
				<v-tabs v-model="activeTab" align-tabs="center">
					<v-tab
						v-for="(tab, index) in tabItems"
						:key="index"
						:text="tab"
						:value="index"
						color="primary"
						density="compact"
					></v-tab>
				</v-tabs>
			</template>
		</v-toolbar>
		<v-snackbar :model-value="converting" location="top" timeout="-1">
			<v-progress-linear
				:model-value="convertProgress"
				:indeterminate="convertProgress === 0"
				color="secondary"
				height="18"
			>
				<template v-slot:default="{value}">
					<span class="text-caption">Converting to AAC… {{ Math.round(value) }}%</span>
				</template>
			</v-progress-linear>
		</v-snackbar>

		<perfect-scrollbar style="height: calc(100vh - 230px)">
			<AssetManagementGlobalTab v-if="activeTab === 0" ref="globalTab" />
			<AssetManagementStationTab v-else-if="activeTab === 1" />
		</perfect-scrollbar>

		<v-card-actions>
			<v-btn variant="flat" color="blue" prepend-icon="mdi-plus" @click="handleNewAsset"> New Asset </v-btn>
			<v-btn
				variant="flat"
				color="error"
				prepend-icon="mdi-close"
				@click.stop.once="deleteAsset"
				:disabled="mode === 'newAsset' || !deleteAssetFeatureEnabled"
			>
				Delete Asset
			</v-btn>
			<v-menu>
				<template v-slot:activator="{props}">
					<v-btn
						v-if="downloadAssetFeatureEnabled"
						variant="flat"
						color="black"
						prepend-icon="mdi-download"
						append-icon="mdi-chevron-down"
						:loading="converting || downloadLoading"
						:disabled="mode === 'newAsset' || !asset || converting"
						v-bind="props"
					>
						Download
					</v-btn>
				</template>

				<v-list density="compact">
					<v-list-item @click="downloadAsset('original')">
						<template v-slot:prepend>
							<v-icon icon="mdi-file-music-outline"></v-icon>
						</template>
						<v-list-item-title>Original Format</v-list-item-title>
					</v-list-item>

					<v-list-item @click="downloadAsset('aac')">
						<template v-slot:prepend>
							<v-icon icon="mdi-music-box-outline"></v-icon>
						</template>
						<v-list-item-title>AAC (.m4a)</v-list-item-title>
					</v-list-item>
				</v-list>
			</v-menu>

			<v-btn
				variant="flat"
				color="grey"
				prepend-icon="mdi-arrow-up"
				:disabled="mode === 'newAsset'"
				@click="findPreviousEvent()"
			>
				Previous Asset
			</v-btn>
			<v-btn
				variant="flat"
				color="grey"
				prepend-icon="mdi-arrow-down"
				:disabled="mode === 'newAsset'"
				@click="findNextEvent()"
			>
				Next Asset
			</v-btn>
			<v-spacer />

			<v-btn
				variant="flat"
				color="primary"
				:loading="loading"
				:disabled="!editAssetFeatureEnabled"
				@click="saveAsset"
			>
				Save
			</v-btn>
			<v-btn variant="flat" color="error" @click="closeAssetManagement"> Close </v-btn>
		</v-card-actions>
		<DeleteDialog :dialog="deleteDialog" :options="deleteObject" @close="closeDialog" />
	</v-card>
</template>

<script>
/* Modules */
import AssetManagementGlobalTab from "@/components/standaloneModules/AssetManagementGlobalTab.vue";
import AssetManagementStationTab from "@/components/standaloneModules/AssetManagementStationTab.vue";
import DeleteDialog from "@/components/dialogs/DeleteDialog.vue";

/* Enums */
import EventTypes from "@/enums/EventTypes";
import PlaylistTypes from "@/enums/PlaylistTypes";
import Modes from "@/enums/AssetManagementModeTypes";

/* Libraries */
import {uuid} from "vue-uuid";
import axios from "axios";
import _ from "lodash";
import emitter from "@/emitter";
import {FFmpeg} from "@ffmpeg/ffmpeg";
import {fetchFile, toBlobURL} from "@ffmpeg/util";

const ffmpegCoreURL = `${import.meta.env.BASE_URL || "/"}ffmpeg-core/ffmpeg-core.js`.replace("//", "/");
const ffmpegWasmURL = `${import.meta.env.BASE_URL || "/"}ffmpeg-core/ffmpeg-core.wasm`.replace("//", "/");

export default {
	name: "AssetManagement",

	components: {DeleteDialog, AssetManagementStationTab, AssetManagementGlobalTab},

	data() {
		return {
			activeTab: null,
			tabItems: ["Global", "Station"],
			deleteDialog: false,
			deleteObject: {},
			newAsset: {
				AssetTypeID: null,
				PlaylistID: null
			},
			ffmpegInstance: null,
			loading: false,
			converting: false,
			convertProgress: 0,
			downloadLoading: false
		};
	},

	computed: {
		stationID() {
			return this.$store.getters.assetManagementStationID();
		},

		event() {
			return this.$store.getters.getAssetManagementEvent();
		},

		parentEvent() {
			return this.$store.getters.getAssetManagementParentEvent();
		},

		asset() {
			return this.$store.getters.getAssetManagementAsset();
		},

		mode() {
			return this.$store.getters.getAssetManagementMode();
		},

		playlistEvents() {
			return this.$store.getters
				.getPlaylistById({stationID: this.stationID, playlistID: this.event.PlaylistID})
				?.Events.filter(
					e =>
						![EventTypes.Comment, EventTypes.ETM, EventTypes.DVTBlock, EventTypes.Rejoin].includes(
							e.EventType
						)
				);
		},

		childPlaylistEvents() {
			return this.event.ChildPlaylistID
				? this.$store.getters.getPlaylistById({
						stationID: this.stationID,
						playlistID: this.event.ChildPlaylistID
					})?.Events
				: [];
		},

		isSpotBlock() {
			return this.event?.EventType === EventTypes.SpotBlock;
		},

		isSpot() {
			return (
				this.$store.getters.getPlaylistById({stationID: this.stationID, playlistID: this.event.PlaylistID})
					?.PlaylistType === PlaylistTypes.SpotBlock
			);
		},

		eventName() {
			return EventTypes.getName(this.event?.EventType);
		},

		navigateLibrary() {
			return !!this.asset && !this.event;
		},

		editAssetFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_edit",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		deleteAssetFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_delete_type",
				asset_type_id: this.asset.AssetTypeID
			});
		},

		downloadAssetFeatureEnabled() {
			if (_.isNil(this.asset)) {
				return false;
			}
			return this.$store.getters.isFeatureEnabledByAssetTypeID({
				object_id: this.$store.getters.assetManagementStationID(),
				reference: "zettacloud_asset_download",
				asset_type_id: this.asset.AssetTypeID
			});
		}
	},

	methods: {
		handleNewAsset() {
			this.$store.commit("setAssetManagementMode", Modes.NewAsset);
		},

		// Sniff the first few bytes of an audio blob to determine its real format,
		// independent of filename / Content-Type. Returns {extension, mimeType} or null.
		async detectAudioFormat(blob) {
			const buf = await blob.slice(0, 12).arrayBuffer();
			const b = new Uint8Array(buf);
			const eq = (offset, ...bytes) => bytes.every((v, i) => b[offset + i] === v);

			// ID3v2 tag → MP3
			if (eq(0, 0x49, 0x44, 0x33)) {
				return {extension: "mp3", mimeType: "audio/mpeg"};
			}
			// RIFF....WAVE → WAV
			if (eq(0, 0x52, 0x49, 0x46, 0x46) && eq(8, 0x57, 0x41, 0x56, 0x45)) {
				return {extension: "wav", mimeType: "audio/wav"};
			}
			// OggS
			if (eq(0, 0x4f, 0x67, 0x67, 0x53)) {
				return {extension: "ogg", mimeType: "audio/ogg"};
			}
			// fLaC
			if (eq(0, 0x66, 0x4c, 0x61, 0x43)) {
				return {extension: "flac", mimeType: "audio/flac"};
			}
			// FORM....AIFF
			if (eq(0, 0x46, 0x4f, 0x52, 0x4d) && eq(8, 0x41, 0x49, 0x46, 0x46)) {
				return {extension: "aiff", mimeType: "audio/aiff"};
			}
			// ....ftyp → MP4/M4A container
			if (eq(4, 0x66, 0x74, 0x79, 0x70)) {
				return {extension: "m4a", mimeType: "audio/mp4"};
			}
			// ASF/WMA GUID
			if (eq(0, 0x30, 0x26, 0xb2, 0x75)) {
				return {extension: "wma", mimeType: "audio/x-ms-wma"};
			}
			// MPEG audio sync (11 bits set) — distinguish AAC-ADTS vs MP3 via the layer field
			if (b[0] === 0xff && (b[1] & 0xe0) === 0xe0) {
				const layer = (b[1] >> 1) & 0x03;
				if (layer === 0) {
					return {extension: "aac", mimeType: "audio/aac"};
				} // ADTS
				if (layer === 1) {
					return {extension: "mp3", mimeType: "audio/mpeg"};
				} // Layer III
			}

			// Fall back to the blob's reported MIME type
			const type = blob.type || "";
			if (type.includes("mpeg")) {
				return {extension: "mp3", mimeType: type};
			}
			if (type.includes("aac")) {
				return {extension: "aac", mimeType: type};
			}
			if (type.includes("mp4") || type.includes("m4a")) {
				return {extension: "m4a", mimeType: type};
			}
			if (type.includes("wav")) {
				return {extension: "wav", mimeType: type};
			}
			if (type.includes("ogg")) {
				return {extension: "ogg", mimeType: type};
			}
			if (type.includes("flac")) {
				return {extension: "flac", mimeType: type};
			}
			return null;
		},

		async getFFmpeg() {
			if (this.ffmpegInstance) {
				return this.ffmpegInstance;
			}

			const ffmpeg = new FFmpeg();

			ffmpeg.on("progress", ({progress}) => {
				this.convertProgress = Math.max(0, Math.min(100, progress * 100));
			});

			ffmpeg.on("error", error => console.error("[ffmpeg error]", error));

			try {
				await ffmpeg.load({
					coreURL: await toBlobURL(ffmpegCoreURL, "text/javascript"),
					wasmURL: await toBlobURL(ffmpegWasmURL, "application/wasm")
				});

				this.ffmpegInstance = ffmpeg;
				return this.ffmpegInstance;
			} catch (error) {
				console.error("Failed to load FFmpeg resources:", error);
				throw error;
			}
		},

		async downloadAsset(targetFormat = "original") {
			this.downloadLoading = true;
			return this.$store
				.dispatch("getAssetAudioUrl", this.asset)
				.then(response => {
					// Fetch the file as a blob from S3
					return axios.get(response.data.url, {responseType: "blob"}).then(async blobResponse => {
						let finalBlob = blobResponse.data;
						let fileName = this.asset.OriginalFileName || `${this.asset.Title}`;

						const hasExtension = /\.[a-z0-9]{2,4}$/i.test(fileName);
						const extension = hasExtension ? fileName.split(".").pop() : null;

						if (targetFormat === "aac") {
							const detected = await this.detectAudioFormat(finalBlob);

							if (!hasExtension && extension !== detected.extension) {
								const lastDotIndex = fileName.lastIndexOf(".");
								if (lastDotIndex === -1) {
									fileName = `${fileName}.${detected.extension}`;
								} else {
									fileName = `${fileName.substring(0, lastDotIndex)}.${detected.extension}`;
								}
							}

							const alreadyAac = extension === "aac" || extension === "m4a";

							if (!alreadyAac) {
								this.convertProgress = 0;
								this.converting = true;
								try {
									const ffmpeg = await this.getFFmpeg();
									const inputName = "input_track";
									const outputName = "output.m4a";

									await ffmpeg.writeFile(inputName, await fetchFile(finalBlob));
									await ffmpeg.exec(["-i", inputName, "-c:a", "aac", "-b:a", "192k", outputName]);

									const data = await ffmpeg.readFile(outputName);
									finalBlob = new Blob([data.buffer || data], {type: "audio/mp4"});

									const baseName = fileName.substring(0, fileName.lastIndexOf(".")) || fileName;
									fileName = `${baseName}.m4a`;

									await ffmpeg.deleteFile(inputName);
									await ffmpeg.deleteFile(outputName);
								} catch (ffmpegError) {
									console.error("FFmpeg conversion failed, falling back to original:", ffmpegError);
									finalBlob = blobResponse.data;
								}
							}
						}
						// If target is "original", only detect format if the extension is missing
						else if (!extension) {
							const detected = await this.detectAudioFormat(finalBlob);
							if (detected) {
								fileName = `${fileName}.${detected.extension}`;
							}
						}

						// Trigger browser download
						const objectURL = window.URL.createObjectURL(finalBlob);
						const link = document.createElement("a");
						link.href = objectURL;
						link.download = fileName;
						document.body.appendChild(link);
						link.click();
						link.remove();
						window.URL.revokeObjectURL(objectURL);
					});
				})
				.catch(error => {
					console.error(error);
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: "Failed to download asset"
					});
				})
				.finally(() => {
					this.downloadLoading = false;
					this.converting = false;
				});
		},

		findNextEvent(index) {
			if (this.navigateLibrary) {
				const libraryAssets = this.$store.getters.getLibraryAssets().data;
				let index = libraryAssets.findIndex(a => a.AssetID === this.asset.AssetID);
				if (index + 1 < libraryAssets.length) {
					this.$store.commit("setAssetManagementAsset", libraryAssets[index + 1]);
				} else {
					this.$store.commit("setAssetManagementAsset", libraryAssets[0]);
				}
			} else {
				let nextEvent;
				if (!index) {
					index = this.playlistEvents.findIndex(pe => pe.EventID === this.event.EventID);
					if (index + 1 >= this.playlistEvents.length && this.parentEvent?.PlaylistID) {
						let parentPlaylist = this.$store.getters.getPlaylistById({
							stationID: this.stationID,
							playlistID: this.parentEvent.PlaylistID
						});
						let parentIndex = parentPlaylist.Events.findIndex(
							pe => pe.EventID === this.parentEvent.EventID
						);
						nextEvent = parentPlaylist.Events[parentIndex + 1];
					} else {
						index = this.playlistEvents.findIndex(pe => pe.EventID === this.event.EventID);
						if (
							index === this.playlistEvents.length - 1 &&
							this.parentEvent?.EventType !== EventTypes.SpotBlock
						) {
							this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
								message: "No next asset found in playlist"
							});
							return;
						}
						nextEvent = this.playlistEvents[index + 1];
					}
				} else {
					nextEvent = this.playlistEvents[index + 1];
				}

				if (nextEvent) {
					if (
						![EventTypes.ETM, EventTypes.Comment, EventTypes.DVTBlock].includes(nextEvent.EventType) &&
						nextEvent?.Asset
					) {
						this.$store.commit("setAssetManagementEvent", nextEvent);
						this.$store.commit("setAssetManagementAsset", nextEvent.Asset);
					} else {
						this.findNextEvent(index + 1);
					}
				}
			}
		},

		findPreviousEvent(index) {
			if (this.navigateLibrary) {
				const libraryAssets = this.$store.getters.getLibraryAssets().data;
				let index = libraryAssets.findIndex(a => a.AssetID === this.asset.AssetID);
				if (index !== 0) {
					this.$store.commit("setAssetManagementAsset", libraryAssets[index - 1]);
				} else {
					this.$store.commit("setAssetManagementAsset", libraryAssets[libraryAssets.length - 1]);
				}
			} else {
				let previousEvent;
				if (!index) {
					index = this.playlistEvents.findIndex(pe => pe.EventID === this.event.EventID);
					if (index - 1 < 0 && this.parentEvent?.PlaylistID) {
						let parentPlaylist = this.$store.getters.getPlaylistById({
							stationID: this.stationID,
							playlistID: this.parentEvent.PlaylistID
						});
						let parentIndex = parentPlaylist.Events.findIndex(
							pe => pe.EventID === this.parentEvent.EventID
						);
						previousEvent = parentPlaylist.Events[parentIndex - 1];
					} else {
						index = this.playlistEvents.findIndex(pe => pe.EventID === this.event.EventID);
						if (index === 0 && this.parentEvent?.EventType !== EventTypes.SpotBlock) {
							this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
								message: "No previous asset found in playlist"
							});
							return;
						}
						previousEvent = this.playlistEvents[index - 1];
					}
				} else {
					previousEvent = this.playlistEvents[index - 1];
				}

				if (previousEvent) {
					if (
						![EventTypes.ETM, EventTypes.Comment, EventTypes.DVTBlock].includes(previousEvent.EventType) &&
						previousEvent?.Asset
					) {
						this.$store.commit("setAssetManagementEvent", previousEvent);
						this.$store.commit("setAssetManagementAsset", previousEvent.Asset);
					} else {
						this.findPreviousEvent(index - 1);
					}
				}
			}
		},

		async saveAsset() {
			this.loading = true;
			if (this.activeTab === 0) {
				try {
					const globalTabData = await this.$refs.globalTab.getData();

					const payload = this.buildPayload(
						globalTabData,
						this.mode === Modes.NewAsset ? this.newAsset.AssetTypeID : this.asset.AssetTypeID,
						this.mode === Modes.NewAsset ? null : this.asset.AssetID
					);
					const importFile = globalTabData.marksData.ImportFile ?? globalTabData.extraData.ImportFile;
					if (importFile) {
						this.importAsset(payload, importFile[0]);
						this.updateAsset(_.omit(payload, ["ImportFile"]));
					} else {
						if (this.mode === Modes.NewAsset) {
							this.createAsset(payload);
						} else {
							this.updateAsset(payload, false);
						}
					}
				} catch (error) {
					console.error(error);
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: "Something went wrong. Please try again."
					});
				}
			} else if (this.activeTab === 1) {
				emitter.emit("saveAsset");
			}
		},

		buildPayload(globalTabData, assetTypeID, assetID) {
			const base =
				this.mode === Modes.NewAsset
					? {AssetID: uuid.v1(), AssetTypeID: assetTypeID}
					: {AssetID: assetID, AssetTypeID: assetTypeID};

			return _.omitBy(
				{
					...globalTabData.globalData,
					...globalTabData.marksData,
					...globalTabData.extraData,
					...base
				},
				_.isNil
			);
		},

		createAsset(newAsset) {
			this.$store
				.dispatch("createAsset", newAsset)
				.then(response => {
					if (response.status === 200) {
						this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
							message: "Asset created successfully!"
						});
						this.$store.commit("setAssetManagementAsset", response.data);
						this.$store.commit("setAssetManagementMode", Modes.Edit);
					}
					this.loading = false;
				})
				.finally(() => (this.loading = false));
		},

		updateAsset(newAsset, requestAsset = true) {
			this.$store
				.dispatch("updateAsset", newAsset)
				.then(asset => {
					if (requestAsset) {
						this.$store.dispatch("getAsset", asset.AssetID).then(asset => {
							this.$store.commit("setAssetManagementAsset", asset);
							this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
								message: "Asset updated successfully"
							});
						});
					} else {
						const updatedAsset = {...this.asset, ...newAsset};
						this.$store.commit("setAssetManagementAsset", updatedAsset);
						this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
							message: "Asset updated successfully"
						});
					}
				})
				.catch(() => {
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: "Asset update failed"
					});
				})
				.finally(() => (this.loading = false));
		},

		deleteAsset() {
			this.deleteObject = {
				description: "Delete Asset",
				object: this.asset,
				objectId: this.asset.AssetID,
				action: "deleteAsset",
				onComplete: () => {
					this.$store.commit("closeAndResetAssetManagement");
				}
			};

			this.deleteDialog = true;
		},

		importAsset(payload, file) {
			return this.$store
				.dispatch("importAsset", {
					AssetID: payload.AssetID,
					AssetTypeID: payload.AssetTypeID,
					Title: payload.Title,
					OriginalFileName: payload.OriginalFileName,
					StationID: this.$store.getters.assetManagementStationID
				})
				.then(response => {
					let audioFormData = new FormData();

					audioFormData.append("key", response.data.fields.key);
					audioFormData.append("bucket", response.data.fields.bucket);
					audioFormData.append("Policy", response.data.fields.Policy);
					audioFormData.append("X-Amz-Algorithm", response.data.fields["X-Amz-Algorithm"]);
					audioFormData.append("X-Amz-Credential", response.data.fields["X-Amz-Credential"]);
					audioFormData.append("X-Amz-Security-Token", response.data.fields["X-Amz-Security-Token"]);
					audioFormData.append("X-Amz-Signature", response.data.fields["X-Amz-Signature"]);
					audioFormData.append("X-Amz-Date", response.data.fields["X-Amz-Date"]);
					audioFormData.append("file", file);

					axios
						.post(response.data.url, audioFormData, {
							headers: {
								"access-control-allow-origin": "*"
							}
						})
						.then(() => {
							this.$store
								.dispatch("getAsset", payload.AssetID)
								.then(asset => {
									if (asset) {
										this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
											message: "Asset imported successfully"
										});

										this.$store.commit("setAssetManagementAsset", asset);
										this.$store.commit("setAssetManagementMode", Modes.Edit);
										this.activeTab = 0;
									} else {
										this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
											message: "Error while importing asset"
										});
										this.$store.commit("clearAssetManagement");
									}
								})
								.finally(() => (this.loading = false));
						});
				});
		},

		closeAssetManagement() {
			this.$store.commit("closeAndResetAssetManagement");
		},

		checkSpotBlock() {
			if (this.isSpotBlock && this.childPlaylistEvents.length > 0) {
				this.$store.commit("setAssetManagementParentEvent", this.event);
				this.$store.commit("setAssetManagementEvent", this.childPlaylistEvents[0]);
			} else if (this.isSpot) {
				let spotBlockEvent = this.$store.getters
					.loadedPlaylists()
					.find(loadedPlaylist => {
						return loadedPlaylist.Events.find(loadedPlaylistEvent => {
							return loadedPlaylistEvent.ChildPlaylistID === this.event.PlaylistID;
						});
					})
					?.Events.find(e => e.ChildPlaylistID === this.event.PlaylistID);

				this.$store.commit("setAssetManagementParentEvent", spotBlockEvent);
			}
		},

		closeDialog() {
			this.deleteDialog = false;
		}
	},

	watch: {
		event() {
			this.checkSpotBlock();
		},

		mode() {
			if (this.mode === Modes.NewAsset) {
				this.newAsset.AssetTypeID = this.asset?.AssetTypeID;
				this.newAsset.PlaylistID = this.event?.PlaylistID;
				this.activeTab = 0;
				this.$store.commit("clearAssetManagement");
			}
		}
	},

	mounted() {
		this.checkSpotBlock();
		emitter.on("stationTab:assetSaved", () => {
			this.loading = false;
		});
	}
};
</script>

<style scoped lang="scss"></style>